import React, {useState} from "react";
import {Box, Button, Chip, IconButton, Link, Paper, Stack, Tooltip, Typography} from "@mui/material";
import {Copy, CreditCard, ExternalLink, HandCoins, Mail, Receipt} from "lucide-react";
import {formatDataIsoBR, formatReais} from "../../../shared/utils/formatters";
import {
    boletoEmRegistro,
    boletoParaPagar,
    getMeioDaCobrancaLabel,
    hojeIso,
    podeReenviarBoleto,
    podeRegistrarPagamento,
    situacaoDaCobranca,
    vencida,
} from "../cobrancasUtils";

// Texto longo de copiar (linha digitável, Pix): quebra em qualquer lugar e não estoura a largura no celular.
function CampoDeCopiar({rotulo, valor}) {
    const [copiado, setCopiado] = useState(false);
    if (!valor) return null;

    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(valor);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        } catch (e) {
            setCopiado(false);
        }
    };

    return (
        <Box>
            <Typography variant="caption" color="text.secondary">{rotulo}</Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Typography variant="body2" sx={{fontFamily: 'monospace', wordBreak: 'break-all', minWidth: 0, flex: 1}}>{valor}</Typography>
                <Tooltip title={copiado ? 'Copiado' : 'Copiar'}>
                    <IconButton size="small" onClick={copiar} aria-label={`Copiar ${rotulo.toLowerCase()}`}>
                        <Copy size={16} />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
}

/*
 * Uma cobrança: meio, valor, situação e, no boleto em aberto, a linha digitável e o Pix para a empresa pagar.
 * As ações aparecem só quando a API aceita: registrar pagamento em aberto e reenviar com boleto pagável.
 */
export default function CobrancaCard({cobranca, hoje = hojeIso(), onRegistrarPagamento, onReenviarBoleto, onAbrir, ocupado = false}) {
    if (!cobranca) return null;

    const situacao = situacaoDaCobranca(cobranca, hoje);
    const boleto = boletoParaPagar(cobranca);
    const Icone = cobranca.meio === 'CARTAO' ? CreditCard : Receipt;
    const encargos = Number(cobranca.valorEncargos || 0);

    return (
        <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{color: 'text.secondary', pt: 0.25}}><Icone size={20} /></Box>
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Typography fontWeight={600}>{getMeioDaCobrancaLabel(cobranca.meio)}</Typography>
                            <Typography fontWeight={700} sx={{whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums'}}>{formatReais(cobranca.valor)}</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            {cobranca.situacao === 'PAGA'
                                ? `Paga em ${formatDataIsoBR(cobranca.pagaEm)}: ${formatReais(cobranca.valorPago)}${encargos > 0 ? `, com ${formatReais(encargos)} de multa e juros` : ''}`
                                : `Vence em ${formatDataIsoBR(cobranca.vencimento)}`}
                        </Typography>
                    </Box>
                    <Chip size="small" label={situacao.rotulo} color={situacao.tom === 'default' ? undefined : situacao.tom} variant="outlined" />
                </Stack>

                {boleto ? (
                    <Stack spacing={1} sx={{bgcolor: 'grey.50', borderRadius: 1.5, p: 1.5}}>
                        <CampoDeCopiar rotulo="Linha digitável" valor={boleto.linhaDigitavel} />
                        <CampoDeCopiar rotulo="Pix copia e cola" valor={boleto.pixCopiaECola} />
                        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
                            {boleto.nossoNumero ? (
                                <Typography variant="caption" color="text.secondary">Nosso número {boleto.nossoNumero}</Typography>
                            ) : null}
                            {boleto.urlPdf ? (
                                <Link href={boleto.urlPdf} target="_blank" rel="noopener noreferrer" variant="body2" sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
                                    Abrir o boleto <ExternalLink size={14} />
                                </Link>
                            ) : null}
                        </Stack>
                        {vencida(cobranca, hoje) ? (
                            <Typography variant="caption" color="text.secondary">
                                Pago depois do vencimento, o boleto cobra multa de 2% e juros de 1% ao mês.
                            </Typography>
                        ) : null}
                    </Stack>
                ) : null}

                {boletoEmRegistro(cobranca) ? (
                    <Typography variant="body2" color="text.secondary">
                        O boleto ainda está sendo registrado no banco. A linha digitável e o Pix aparecem aqui quando o banco confirmar.
                    </Typography>
                ) : null}

                {(onRegistrarPagamento && podeRegistrarPagamento(cobranca)) || (onReenviarBoleto && podeReenviarBoleto(cobranca)) || onAbrir ? (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                        {onAbrir ? (
                            <Button size="small" onClick={() => onAbrir(cobranca)}>Ver detalhes</Button>
                        ) : null}
                        {onReenviarBoleto && podeReenviarBoleto(cobranca) ? (
                            <Button size="small" variant="outlined" startIcon={<Mail size={14} />} disabled={ocupado} onClick={() => onReenviarBoleto(cobranca)}>
                                Reenviar boleto
                            </Button>
                        ) : null}
                        {onRegistrarPagamento && podeRegistrarPagamento(cobranca) ? (
                            <Button size="small" variant="outlined" color="success" startIcon={<HandCoins size={14} />} disabled={ocupado} onClick={() => onRegistrarPagamento(cobranca)}>
                                Registrar pagamento
                            </Button>
                        ) : null}
                    </Stack>
                ) : null}
            </Stack>
        </Paper>
    );
}
